/**
 * EventPady+ TanStack Query Client
 *
 * Centralised QueryClient with:
 *   - Global error handlers that display Sonner toast notifications
 *   - 60-second default stale time
 *   - Retry only on network errors (no response); never on 4xx / 5xx
 *
 * Exported as a singleton so App.tsx and tests share the same instance.
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isAxiosError } from 'axios'
import type { ApiError } from '@/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    return (
      error.response?.data?.error?.message ??
      'A network error occurred. Please try again.'
    )
  }
  if (error instanceof Error) return error.message
  return 'An unexpected error occurred.'
}

function isNetworkError(error: unknown): boolean {
  return isAxiosError(error) && !error.response
}

// ---------------------------------------------------------------------------
// Query client
// ---------------------------------------------------------------------------

export const queryClient = new QueryClient({
  // ── QueryCache: fires for background re-fetches that fail silently ─────
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only toast when we already have stale data on screen — the user
      // sees content but the refresh failed. Fresh load failures surface
      // inline via the component's error boundary or error state.
      if (query.state.data !== undefined) {
        toast.error(getErrorMessage(error), { id: 'query-error' })
      }
    },
  }),

  // ── MutationCache: fires for all mutation errors ───────────────────────
  mutationCache: new MutationCache({
    onError: (error) => {
      // Toast only for network-level failures; validation errors (422) and
      // business rule rejections are handled inline by the form/component.
      if (isNetworkError(error)) {
        toast.error('Network error. Please check your connection.', {
          id: 'mutation-network-error',
        })
      }
    },
  }),

  defaultOptions: {
    queries: {
      // 60 seconds — balances freshness vs. unnecessary refetches
      staleTime: 60 * 1000,
      // 5 minutes garbage collection window
      gcTime: 5 * 60 * 1000,
      // Retry once for network errors only; 4xx/5xx fail immediately
      retry: (failureCount: number, error: unknown): boolean => {
        if (isAxiosError(error) && error.response) {
          // Server responded — this is not a transient network error
          return false
        }
        return failureCount < 1
      },
      // Suppress refetch on window focus in dev to reduce noise
      refetchOnWindowFocus: import.meta.env.PROD,
    },
    mutations: {
      // Mutations are non-idempotent — never auto-retry
      retry: false,
    },
  },
})
